import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { S } from './styles';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const STEPS = { WELCOME: 'welcome', PASSWORD: 'password', RSVP: 'rsvp' };

export default function InviteWelcomeModal({ event, onClose, onAccepted }) {
  const { token, profile } = useAuth();
  const [step, setStep]         = useState(STEPS.WELCOME);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [plusOnes, setPlusOnes] = useState(0);

  const fmtDate = d => new Date(d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });

  async function handleSetPassword() {
    if (!password) return setError('Please enter a password');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirm) return setError('Passwords do not match');

    setLoading(true); setError('');
    try {
      const { error: pwErr } = await supabase.auth.updateUser({ password });
      if (pwErr) throw pwErr;
      setStep(STEPS.RSVP);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRSVP(status) {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/attendance/${event.id}/${status}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plus_ones: plusOnes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      if (status === 'accept') {
        onAccepted();
      } else {
        onClose();
      }
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(44,26,14,.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 500, padding: 16,
    }}>
      <div style={{
        background: 'white', borderRadius: 24,
        width: '100%', maxWidth: 480,
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(44,26,14,.25)',
      }}>

        {/* Header */}
        <div style={{
          background: '#2C1A0E',
          padding: '32px 32px 28px',
        }}>
          <p style={{
            fontFamily: 'Cormorant Garamond,serif',
            fontSize: 12, letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: 'rgba(250,247,242,.4)',
            marginBottom: 12,
          }}>
            What Can We Bring?
          </p>
          <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{event.emoji || '🍽️'}</span>
          <h2 style={{
            fontFamily: 'Cormorant Garamond,serif',
            fontSize: 28, color: '#FAF7F2',
            lineHeight: 1.2, marginBottom: 6,
          }}>
            {event.title}
          </h2>
          <p style={{ fontSize: 13, color: 'rgba(250,247,242,.55)' }}>
            {fmtDate(event.date)} · {event.time}
          </p>
          <p style={{ fontSize: 13, color: 'rgba(250,247,242,.4)', marginTop: 2 }}>
            Hosted by {event.profiles?.full_name}
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: 32 }}>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
            {[STEPS.WELCOME, STEPS.PASSWORD, STEPS.RSVP].map((s, i) => (
              <div key={s} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: [STEPS.WELCOME, STEPS.PASSWORD, STEPS.RSVP].indexOf(step) >= i
                  ? '#C4612A' : '#E5D9C8',
                transition: 'background .3s',
              }}/>
            ))}
          </div>

          {error && (
            <div style={{
              background: '#FFEBEE', color: '#B71C1C',
              padding: '10px 14px', borderRadius: 8,
              fontSize: 13, marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {/* Step 1 — Welcome */}
          {step === STEPS.WELCOME && (
            <div>
              <h3 style={{
                fontFamily: 'Cormorant Garamond,serif',
                fontSize: 24, marginBottom: 10,
              }}>
                You're invited, {profile?.full_name?.split(' ')[0] || 'friend'}!
              </h3>
              <p style={{ fontSize: 14, color: '#5A3E2B', lineHeight: 1.7, marginBottom: 12 }}>
                You've been invited to a dinner party. Before you RSVP, let's get your account set up so you can come back any time.
              </p>
              {event.theme && (
                <div style={{
                  background: '#FAEEE8',
                  border: '1px solid rgba(196,97,42,.2)',
                  borderRadius: 10, padding: '12px 14px',
                  fontSize: 13, color: '#7B2D0D',
                  fontStyle: 'italic', marginBottom: 20,
                }}>
                  🎨 {event.theme}
                </div>
              )}
              <button
                style={{ ...S.btnPrimary, width: '100%', padding: '12px' }}
                onClick={() => setStep(STEPS.PASSWORD)}>
                Get started →
              </button>
            </div>
          )}

          {/* Step 2 — Set password */}
          {step === STEPS.PASSWORD && (
            <div>
              <h3 style={{
                fontFamily: 'Cormorant Garamond,serif',
                fontSize: 24, marginBottom: 8,
              }}>
                Set your password
              </h3>
              <p style={{ fontSize: 13, color: '#7A6555', marginBottom: 20, lineHeight: 1.6 }}>
                Create a password so you can sign back in any time to manage your dishes and see event updates.
              </p>

              <label style={S.label}>New password</label>
              <input
                type="password"
                style={S.input}
                placeholder="At least 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
              />

              <label style={S.label}>Confirm password</label>
              <input
                type="password"
                style={S.input}
                placeholder="Repeat your password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
              />

              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btnGhost} onClick={() => setStep(STEPS.WELCOME)}>
                  Back
                </button>
                <button
                  style={{
                    ...S.btnPrimary, flex: 1, padding: '12px',
                    opacity: loading ? 0.6 : 1,
                  }}
                  disabled={loading}
                  onClick={handleSetPassword}>
                  {loading ? 'Saving…' : 'Set password →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — RSVP */}
          {step === STEPS.RSVP && (
            <div>
              <h3 style={{
                fontFamily: 'Cormorant Garamond,serif',
                fontSize: 24, marginBottom: 8,
              }}>
                Will you be there?
              </h3>
             <p style={{ fontSize: 13, color: '#7A6555', marginBottom: 24, lineHeight: 1.6 }}>
                Let {event.profiles?.full_name} know if you can make it. You can suggest a dish to bring once you accept.
              </p>

              <div style={{ marginBottom: 24 }}>
                <label style={S.label}>How many people are you bringing (including yourself)?</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #E5D9C8', background: 'white', fontSize: 20, cursor: plusOnes === 0 ? 'not-allowed' : 'pointer', color: plusOnes === 0 ? '#E5D9C8' : '#2C1A0E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setPlusOnes(p => Math.max(0, p - 1))}
                    disabled={plusOnes === 0}>
                    −
                  </button>
                  <span style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 32, color: '#2C1A0E', minWidth: 32, textAlign: 'center' }}>
                    {plusOnes + 1}
                  </span>
                  <button
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #E5D9C8', background: 'white', fontSize: 20, cursor: 'pointer', color: '#2C1A0E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setPlusOnes(p => p + 1)}>
                    +
                  </button>
                  <span style={{ fontSize: 13, color: '#7A6555' }}>
                    {plusOnes === 0 ? 'Just me' : `You + ${plusOnes} guest${plusOnes > 1 ? 's' : ''}`}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  style={{
                    ...S.btnPrimary,
                    background: '#5B7A5B',
                    padding: '14px',
                    fontSize: 15,
                    opacity: loading ? 0.6 : 1,
                  }}
                  disabled={loading}
                  onClick={() => handleRSVP('accept')}>
                  ✓ Yes, I'll be there!
                </button>
                <button
                  style={{
                    ...S.btnGhost,
                    padding: '14px',
                    fontSize: 15,
                    opacity: loading ? 0.6 : 1,
                  }}
                  disabled={loading}
                  onClick={() => handleRSVP('cancel')}>
                  I can't make it this time
                </button>
              </div>

              <p style={{ fontSize: 11, color: '#7A6555', marginTop: 16, textAlign: 'center' }}>
                You can change your RSVP later from the event page
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}